// frontend/src/widgets/CameraEnrollmentWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-21
// Updated:      2026-09-07 14:49 BST
//
// Description:
//   Camera enrolment widget.
//
//   Responsibilities:
//     - Enrol new AiQ camera devices for ingest onboarding
//     - Edit core enrolled camera details
//     - Persist ingest_enabled and ingest_key_hash
//     - Surface safe device state including has_ingest_key and last ingest use
//
// Mobile Responsiveness (2026-09-07):
//   - Preserves the existing desktop summary, enrol/edit form, and camera table.
//   - Reflows summary cards and form controls for screens below 768px.
//   - Uses the existing camera table DOM as mobile cards; no duplicate list render.
//   - Measures the first rendered camera card so one complete device record is
//     visible in the mobile list viewport before scrolling to the next record.
//   - Expands the nearest ReactGridLayout host to the widget's natural mobile
//     height because mobile widgets do not expose the desktop resize handle.
//   - Keeps camera API paths, validation, enrol/update behaviour, and safe
//     ingest-key handling unchanged.
//
// Notes:
//   - This widget is intentionally lean and focused on enrolment only.
//   - It follows the practical minimum shape used in manual SQL inserts.
//   - The stored hash is never returned by the API; only has_ingest_key is shown.
//   - Leave ingest_key_hash blank on edit to preserve the existing stored hash.
//   - Extended device metadata belongs in a separate admin widget if needed later.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const MOBILE_BREAKPOINT = 768;
const MOBILE_CAMERA_FALLBACK_HEIGHT = 380;

const EMPTY_FORM = {
  serial_no: "",
  mac_address: "",
  camera_name: "",
  model_no: "",
  ingest_key_hash: "",
  is_active: true,
  ingest_enabled: true,
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const normalizeOptionalString = (value) => {
  const next = String(value ?? "").trim();
  return next === "" ? null : next;
};

const buttonStyle = (theme, variant = "action", disabled = false) => ({
  backgroundColor:
    variant === "general"
      ? theme.button_general?.normal || "#4b5563"
      : theme.button_action?.normal || "#000",
  color: "#fff",
  padding: "9px 14px",
  borderRadius: "8px",
  border: "none",
  fontWeight: 700,
  fontFamily: "var(--font-body)",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.65 : 1,
  whiteSpace: "nowrap",
});

const applyHover = (e, theme, variant = "action") => {
  e.currentTarget.style.backgroundColor =
    variant === "general"
      ? theme.button_general?.hover || "#374151"
      : theme.button_action?.hover || "#333";
};

const applyNormal = (e, theme, variant = "action") => {
  e.currentTarget.style.backgroundColor =
    variant === "general"
      ? theme.button_general?.normal || "#4b5563"
      : theme.button_action?.normal || "#000";
};

const applyClick = (e, theme, variant = "action") => {
  e.currentTarget.style.backgroundColor =
    variant === "general"
      ? theme.button_general?.click || "#1f2937"
      : theme.button_action?.click || "#111";
};

const fieldStyle = (isMobile = false) => ({
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  padding: "10px 12px",
  backgroundColor: "#fff",
  fontFamily: "var(--font-body)",
  fontSize: isMobile ? "16px" : "0.94rem",
  boxSizing: "border-box",
});

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 700,
  color: "#333",
};

const mobileFieldLabelStyle = {
  fontSize: "0.72rem",
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.035em",
  marginBottom: "4px",
};

const CameraEnrollmentWidget = () => {
  const { theme } = useTheme();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [mobileCameraViewportHeight, setMobileCameraViewportHeight] =
    useState(null);

  const rootRef = useRef(null);
  const firstCameraRowRef = useRef(null);

  const isEditMode = editingId !== null;

  const loadDevices = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");

    try {
      const res = await axiosInstance.get("anpr/cameras");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load camera devices", err);
      setError(err?.response?.data?.detail || "Failed to load camera devices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || rows.length === 0) {
      setMobileCameraViewportHeight(null);
      return undefined;
    }

    let frameId = null;
    let observer = null;

    const measureFirstCameraCard = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const row = firstCameraRowRef.current;
        if (!row) return;

        const height = Math.ceil(row.getBoundingClientRect().height);
        if (height > 0) {
          setMobileCameraViewportHeight(height);
        }
      });
    };

    measureFirstCameraCard();
    window.addEventListener("resize", measureFirstCameraCard);

    if (typeof ResizeObserver !== "undefined" && firstCameraRowRef.current) {
      observer = new ResizeObserver(measureFirstCameraCard);
      observer.observe(firstCameraRowRef.current);
    }

    return () => {
      window.removeEventListener("resize", measureFirstCameraCard);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isMobile, rows]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [isMobile, mobileCameraViewportHeight, rows.length, editingId, error, success]);

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.total += 1;
        if (row.is_active) acc.active += 1;
        if (row.ingest_enabled) acc.ingestEnabled += 1;
        if (row.has_ingest_key) acc.hasKey += 1;
        return acc;
      },
      { total: 0, active: 0, ingestEnabled: 0, hasKey: 0 }
    );
  }, [rows]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (row) => {
    setSuccess("");
    setError("");
    setEditingId(row.id);
    setForm({
      serial_no: row.serial_no || "",
      mac_address: row.mac_address || "",
      camera_name: row.camera_name || "",
      model_no: row.model_no || "",
      ingest_key_hash: "",
      is_active: Boolean(row.is_active),
      ingest_enabled: Boolean(row.ingest_enabled),
    });
  };

  const buildPayload = () => {
    const payload = {
      serial_no: normalizeOptionalString(form.serial_no),
      mac_address: normalizeOptionalString(form.mac_address),
      camera_name: normalizeOptionalString(form.camera_name),
      model_no: normalizeOptionalString(form.model_no),
      is_active: Boolean(form.is_active),
      ingest_enabled: Boolean(form.ingest_enabled),
    };

    const hashValue = String(form.ingest_key_hash || "").trim();
    if (hashValue) {
      payload.ingest_key_hash = hashValue;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = buildPayload();

      if (!payload.camera_name) {
        throw new Error("Camera name is required.");
      }

      if (!payload.serial_no) {
        throw new Error("Serial number is required.");
      }

      if (!payload.mac_address) {
        throw new Error("MAC address is required.");
      }

      if (!payload.model_no) {
        throw new Error("Model is required.");
      }

      if (!isEditMode && !payload.ingest_key_hash) {
        throw new Error("Ingest key hash is required for enrolment.");
      }

      if (isEditMode) {
        await axiosInstance.patch(`anpr/cameras/${editingId}`, payload);
        setSuccess("Camera updated.");
      } else {
        await axiosInstance.post("anpr/cameras", payload);
        setSuccess("Camera enrolled.");
      }

      resetForm();
      await loadDevices({ silent: true });
    } catch (err) {
      console.error("Failed to save camera device", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to save camera device."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "8px 4px", color: "#444" }}>
        Loading camera devices...
      </div>
    );
  }

  const formHeaderActionsStyle = {
    display: "flex",
    gap: "8px",
    flexDirection: isMobile ? "column" : "row",
    width: isMobile ? "100%" : "auto",
  };

  const fullMobileButtonStyle = (base) => ({
    ...base,
    ...(isMobile
      ? {
          width: "100%",
          minHeight: "44px",
          whiteSpace: "normal",
        }
      : {}),
  });

  return (
    <div
      ref={rootRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        height: isMobile ? "auto" : "100%",
        minHeight: 0,
        width: "100%",
        overflow: isMobile ? "visible" : undefined,
      }}
    >
      {error && (
        <div
          style={{
            backgroundColor: "#fff4f4",
            border: "1px solid #f0caca",
            color: "#a10000",
            borderRadius: "8px",
            padding: "10px 12px",
            fontSize: "0.95rem",
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            backgroundColor: "#ecfdf5",
            border: "1px solid #a7f3d0",
            color: "#065f46",
            borderRadius: "8px",
            padding: "10px 12px",
            fontSize: "0.95rem",
            overflowWrap: "anywhere",
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(4, minmax(0, 1fr))",
          gap: "12px",
        }}
      >
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "12px",
            backgroundColor: "#fff",
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: 700 }}>
            Total devices
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>
            {summary.total}
          </div>
        </div>
        <div
          style={{
            border: "1px solid #dbeafe",
            borderRadius: "10px",
            padding: "12px",
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>Active</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>
            {summary.active}
          </div>
        </div>
        <div
          style={{
            border: "1px solid #fde68a",
            borderRadius: "10px",
            padding: "12px",
            backgroundColor: "#fffbeb",
            color: "#92400e",
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>
            Ingest enabled
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>
            {summary.ingestEnabled}
          </div>
        </div>
        <div
          style={{
            border: "1px solid #a7f3d0",
            borderRadius: "10px",
            padding: "12px",
            backgroundColor: "#ecfdf5",
            color: "#065f46",
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>
            Key configured
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>
            {summary.hasKey}
          </div>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          backgroundColor: "#fff",
          padding: isMobile ? "14px" : "16px",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: "12px",
            marginBottom: "14px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 800,
                color: theme.accent_color || "#000",
              }}
            >
              {isEditMode ? `Edit Camera #${editingId}` : "Enroll Camera"}
            </div>
            <div
              style={{
                fontSize: "0.9rem",
                color: "#666",
                marginTop: "4px",
                overflowWrap: "anywhere",
              }}
            >
              Lean enrolment only. This matches the practical SQL insert shape you
              are actually using.
            </div>
          </div>

          <div style={formHeaderActionsStyle}>
            <button
              type="button"
              onClick={() => loadDevices({ silent: true })}
              style={fullMobileButtonStyle(buttonStyle(theme, "general"))}
              onMouseEnter={(e) => applyHover(e, theme, "general")}
              onMouseLeave={(e) => applyNormal(e, theme, "general")}
              onMouseDown={(e) => applyClick(e, theme, "general")}
              onMouseUp={(e) => applyHover(e, theme, "general")}
            >
              Refresh
            </button>
            {isEditMode && (
              <button
                type="button"
                onClick={resetForm}
                style={fullMobileButtonStyle(buttonStyle(theme, "general"))}
                onMouseEnter={(e) => applyHover(e, theme, "general")}
                onMouseLeave={(e) => applyNormal(e, theme, "general")}
                onMouseDown={(e) => applyClick(e, theme, "general")}
                onMouseUp={(e) => applyHover(e, theme, "general")}
              >
                Cancel edit
              </button>
            )}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "14px" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(2, minmax(0, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label
                style={{ display: "block", fontWeight: 700, marginBottom: "6px" }}
              >
                Camera name
              </label>
              <input
                value={form.camera_name}
                onChange={(e) => handleChange("camera_name", e.target.value)}
                style={fieldStyle(isMobile)}
                placeholder="PCA-ANPR-Cam-2"
              />
            </div>
            <div>
              <label
                style={{ display: "block", fontWeight: 700, marginBottom: "6px" }}
              >
                Model
              </label>
              <input
                value={form.model_no}
                onChange={(e) => handleChange("model_no", e.target.value)}
                style={fieldStyle(isMobile)}
                placeholder="AiQ Nano"
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(2, minmax(0, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label
                style={{ display: "block", fontWeight: 700, marginBottom: "6px" }}
              >
                Serial number
              </label>
              <input
                value={form.serial_no}
                onChange={(e) => handleChange("serial_no", e.target.value)}
                style={fieldStyle(isMobile)}
                placeholder="K1005253"
              />
            </div>
            <div>
              <label
                style={{ display: "block", fontWeight: 700, marginBottom: "6px" }}
              >
                MAC address
              </label>
              <input
                value={form.mac_address}
                onChange={(e) => handleChange("mac_address", e.target.value)}
                style={fieldStyle(isMobile)}
                placeholder="3C:6D:66:A9:8B:29"
              />
            </div>
          </div>

          <div>
            <label
              style={{ display: "block", fontWeight: 700, marginBottom: "6px" }}
            >
              Ingest key hash
            </label>
            <input
              value={form.ingest_key_hash}
              onChange={(e) => handleChange("ingest_key_hash", e.target.value)}
              style={fieldStyle(isMobile)}
              placeholder="64-char SHA-256 hash"
            />
            <div
              style={{
                marginTop: "4px",
                fontSize: "0.8rem",
                color: "#666",
                overflowWrap: "anywhere",
              }}
            >
              {isEditMode
                ? "Leave blank to keep the existing stored hash unchanged."
                : "This stores the SHA-256 hash used for camera ingest authentication."}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "8px" : "24px",
              alignItems: isMobile ? "stretch" : "center",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{
                ...checkboxLabelStyle,
                ...(isMobile
                  ? {
                      minHeight: "44px",
                      padding: "6px 0",
                    }
                  : {}),
              }}
            >
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => handleChange("is_active", e.target.checked)}
                style={isMobile ? { width: 20, height: 20 } : undefined}
              />
              Device active
            </label>
            <label
              style={{
                ...checkboxLabelStyle,
                ...(isMobile
                  ? {
                      minHeight: "44px",
                      padding: "6px 0",
                    }
                  : {}),
              }}
            >
              <input
                type="checkbox"
                checked={form.ingest_enabled}
                onChange={(e) => handleChange("ingest_enabled", e.target.checked)}
                style={isMobile ? { width: 20, height: 20 } : undefined}
              />
              Ingest enabled
            </label>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: isMobile ? "stretch" : "flex-end",
            }}
          >
            <button
              type="submit"
              disabled={saving}
              style={fullMobileButtonStyle(buttonStyle(theme, "action", saving))}
              onMouseEnter={(e) =>
                !saving && applyHover(e, theme, "action")
              }
              onMouseLeave={(e) => applyNormal(e, theme, "action")}
              onMouseDown={(e) =>
                !saving && applyClick(e, theme, "action")
              }
              onMouseUp={(e) => !saving && applyHover(e, theme, "action")}
            >
              {saving
                ? "Saving..."
                : isEditMode
                  ? "Update camera"
                  : "Enroll camera"}
            </button>
          </div>
        </form>
      </div>

      <div
        style={{
          overflowX: isMobile ? "hidden" : "auto",
          overflowY: isMobile && rows.length > 0 ? "auto" : "visible",
          overscrollBehavior: isMobile ? "contain" : undefined,
          scrollSnapType: isMobile && rows.length > 0 ? "y mandatory" : undefined,
          height:
            isMobile && rows.length > 0
              ? `${mobileCameraViewportHeight || MOBILE_CAMERA_FALLBACK_HEIGHT}px`
              : "auto",
          minHeight:
            isMobile && rows.length > 0
              ? `${mobileCameraViewportHeight || MOBILE_CAMERA_FALLBACK_HEIGHT}px`
              : undefined,
          maxHeight:
            isMobile && rows.length > 0
              ? `${mobileCameraViewportHeight || MOBILE_CAMERA_FALLBACK_HEIGHT}px`
              : undefined,
          border: "1px solid #ececec",
          borderRadius: "10px",
          backgroundColor: "#fff",
          minWidth: 0,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: isMobile ? "separate" : "collapse",
            borderSpacing: 0,
            fontSize: "0.92rem",
            display: isMobile ? "block" : "table",
          }}
        >
          <thead style={isMobile ? { display: "none" } : undefined}>
            <tr style={{ backgroundColor: "#f7f7f7", textAlign: "left" }}>
              <th style={{ padding: "10px 12px" }}>Camera</th>
              <th style={{ padding: "10px 12px" }}>Identifiers</th>
              <th style={{ padding: "10px 12px" }}>State</th>
              <th style={{ padding: "10px 12px" }}>Last ingest</th>
              <th style={{ padding: "10px 12px" }}>Actions</th>
            </tr>
          </thead>
          <tbody style={isMobile ? { display: "block", width: "100%" } : undefined}>
            {rows.length === 0 ? (
              <tr style={isMobile ? { display: "block", width: "100%" } : undefined}>
                <td
                  colSpan={5}
                  style={{
                    padding: "18px 12px",
                    color: "#555",
                    display: isMobile ? "block" : undefined,
                    whiteSpace: "normal",
                  }}
                >
                  No camera devices enrolled yet.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.id}
                  ref={index === 0 ? firstCameraRowRef : null}
                  style={
                    isMobile
                      ? {
                          display: "block",
                          width: "100%",
                          border: "none",
                          backgroundColor: "#fff",
                          scrollSnapAlign: "start",
                          scrollSnapStop: "always",
                        }
                      : { borderTop: "1px solid #efefef" }
                  }
                >
                  <td
                    style={{
                      padding: isMobile ? "12px 14px" : "10px 12px",
                      display: isMobile ? "block" : undefined,
                      borderBottom: isMobile ? "1px solid #efefef" : undefined,
                      minWidth: 0,
                    }}
                  >
                    {isMobile && <div style={mobileFieldLabelStyle}>Camera</div>}
                    <div
                      style={{
                        fontWeight: 800,
                        color: theme.accent_color || "#000",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {row.camera_name || `Camera ${row.id}`}
                    </div>
                    <div
                      style={{
                        color: "#666",
                        fontSize: "0.83rem",
                        overflowWrap: "anywhere",
                      }}
                    >
                      #{row.id} · {row.model_no || "Unknown model"}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: isMobile ? "12px 14px" : "10px 12px",
                      display: isMobile ? "block" : undefined,
                      borderBottom: isMobile ? "1px solid #efefef" : undefined,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {isMobile && (
                      <div style={mobileFieldLabelStyle}>Identifiers</div>
                    )}
                    <div>
                      <strong>Serial:</strong> {row.serial_no || "—"}
                    </div>
                    <div>
                      <strong>MAC:</strong> {row.mac_address || "—"}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: isMobile ? "12px 14px" : "10px 12px",
                      display: isMobile ? "block" : undefined,
                      borderBottom: isMobile ? "1px solid #efefef" : undefined,
                    }}
                  >
                    {isMobile && <div style={mobileFieldLabelStyle}>State</div>}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontWeight: 700,
                          backgroundColor: row.is_active ? "#ecfdf5" : "#f3f4f6",
                          border: row.is_active
                            ? "1px solid #a7f3d0"
                            : "1px solid #d1d5db",
                          color: row.is_active ? "#065f46" : "#4b5563",
                        }}
                      >
                        {row.is_active ? "Active" : "Inactive"}
                      </span>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontWeight: 700,
                          backgroundColor: row.ingest_enabled
                            ? "#eff6ff"
                            : "#f3f4f6",
                          border: row.ingest_enabled
                            ? "1px solid #bfdbfe"
                            : "1px solid #d1d5db",
                          color: row.ingest_enabled ? "#1d4ed8" : "#4b5563",
                        }}
                      >
                        {row.ingest_enabled ? "Ingest on" : "Ingest off"}
                      </span>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontWeight: 700,
                          backgroundColor: row.has_ingest_key
                            ? "#fffbeb"
                            : "#fef2f2",
                          border: row.has_ingest_key
                            ? "1px solid #fde68a"
                            : "1px solid #fecaca",
                          color: row.has_ingest_key ? "#92400e" : "#991b1b",
                        }}
                      >
                        {row.has_ingest_key ? "Key set" : "No key"}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: isMobile ? "12px 14px" : "10px 12px",
                      whiteSpace: isMobile ? "normal" : "nowrap",
                      display: isMobile ? "block" : undefined,
                      borderBottom: isMobile ? "1px solid #efefef" : undefined,
                    }}
                  >
                    {isMobile && (
                      <div style={mobileFieldLabelStyle}>Last ingest</div>
                    )}
                    {formatDateTime(row.ingest_last_used_at)}
                  </td>
                  <td
                    style={{
                      padding: isMobile ? "12px 14px 14px" : "10px 12px",
                      display: isMobile ? "block" : undefined,
                    }}
                  >
                    {isMobile && <div style={mobileFieldLabelStyle}>Actions</div>}
                    <button
                      type="button"
                      onClick={() => handleEdit(row)}
                      style={fullMobileButtonStyle(buttonStyle(theme, "general"))}
                      onMouseEnter={(e) => applyHover(e, theme, "general")}
                      onMouseLeave={(e) => applyNormal(e, theme, "general")}
                      onMouseDown={(e) => applyClick(e, theme, "general")}
                      onMouseUp={(e) => applyHover(e, theme, "general")}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "10px",
          backgroundColor: "#fff",
          padding: "12px 14px",
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontWeight: 800,
            marginBottom: "6px",
            color: theme.accent_color || "#000",
          }}
        >
          Enrolment note
        </div>
        <div
          style={{
            color: "#555",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            overflowWrap: "anywhere",
          }}
        >
          This widget only captures the core device data required to onboard and
          authenticate a camera. Extended metadata such as firmware, app version,
          IP, URL, and location can be backfilled from payloads or managed later in
          a dedicated admin widget if that becomes necessary.
        </div>
      </div>
    </div>
  );
};

export default CameraEnrollmentWidget;
